import { useQuery } from '@tanstack/react-query';
import { listTicketStatuses, type TicketStatusRecord } from '@/lib/api/ticket-statuses.js';

const FALLBACK_CLASSES: Record<string, string> = {
  quote: 'bg-purple-100 text-purple-800',
  pending_review: 'bg-violet-100 text-violet-800',
  intake: 'bg-slate-100 text-slate-700',
  pending_sig: 'bg-amber-100 text-amber-800',
  in_queue: 'bg-teal-100 text-teal-800',
  diagnosing: 'bg-orange-100 text-orange-800',
  waiting_auth: 'bg-amber-100 text-amber-800',
  awaiting_approval: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800',
  waiting_parts: 'bg-orange-100 text-orange-800',
  in_repair: 'bg-blue-100 text-blue-800',
  on_hold: 'bg-slate-100 text-slate-700',
  ready: 'bg-green-100 text-green-800',
  pending_pay: 'bg-green-100 text-green-800',
  closed: 'bg-slate-800 text-white',
  warranty: 'bg-blue-100 text-blue-800',
  cancelled: 'bg-red-100 text-red-800',
  voided: 'bg-red-100 text-red-800',
};

export function formatTicketStatus(status: string, catalog?: TicketStatusRecord[]): string {
  const fromCatalog = catalog?.find((s) => s.slug === status);
  if (fromCatalog) return fromCatalog.name;
  return status.replace(/_/g, ' ');
}

export function useTicketStatusCatalog(includeInactive = false) {
  return useQuery({
    queryKey: ['ticket-statuses', includeInactive ? 'all' : 'active'],
    queryFn: () => listTicketStatuses({ includeInactive }),
    staleTime: 60_000,
  });
}

export function StatusBadge({
  status,
  catalog,
}: {
  status: string;
  catalog?: TicketStatusRecord[];
}) {
  const row = catalog?.find((s) => s.slug === status);
  const label = formatTicketStatus(status, catalog);
  if (row?.color) {
    return (
      <span
        className="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold capitalize text-white"
        style={{ backgroundColor: row.color }}
      >
        {label}
      </span>
    );
  }
  return (
    <span
      className={[
        'inline-flex rounded-full px-2.5 py-1 text-xs font-semibold capitalize',
        FALLBACK_CLASSES[status] ?? 'bg-slate-100 text-slate-700',
      ].join(' ')}
    >
      {label}
    </span>
  );
}
